import React, {useState, useEffect, ChangeEvent} from "react"
import styled from "styled-components"
import Button from "@irati/ui/src/Button"
import authentication from "@irati/auth"
import Validation from "@irati/utils/validation"
import {match as Match} from "react-router-dom"
import Flex from "@irati/ui/src/Flex"
import {Input, ValidationButton} from "@irati/ui/src/FormItem"
import {Row, Col} from "react-bootstrap"
import Error from "@irati/ui/src/Loading/Error"
import {Body, H2} from "@irati/ui/src/Typography"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import FilterBy from "@irati/ui/src/FilterBy"
import teamName from "@irati/identity/utils/team/teamName"
import Message from "@irati-web/fields/recognition/Message"
import {useBillingUnitsQuery} from "@irati/billing/hooks/bu"
import {useCreateCustomRewardMutation, useCustomRewardQuery, useUpdateCustomRewardMutation} from "@irati/custom-reward/hooks"
import Spacing from "@irati/ui/src/Spacing"
import History, {historyContext} from "@irati/utils/history"
import {SETTINGS_CUSTOM_REWARDS, SETTINGS_CUSTOM_REWARDS_EDIT} from "../../../routes/routeConfig"
import Loading from "@irati/ui/src/Loading"
import {RouteContext} from "@irati/utils/history/utils/context"
import GenericModal from "@irati/ui/src/GenericModal"
import CancelConfirm from "./CancelConfirm"
const ALL_LOCATIONS = "ALL"
interface FilterCheckboxOption {
  uuid: string
  name: string
  value: string | string[]
  isChecked: boolean
}
interface Props {
  match: Match<{uuid: string}>
}
const POLICY_MAX_LENGTH = 2000
const REWARD_TITLE_MAX_LENGTH = 40
const defaultState = {
  title: "",
  policy: "",
  redemptionInstructions: ""
}
export const StyledFilterBy = styled(FilterBy)`
  width: 100%;
  max-width: 100%;
`
const CustomRewardForm = (props: Props, context: RouteContext) => {
  const isEdit = props.match.path.includes(SETTINGS_CUSTOM_REWARDS_EDIT)
  const {formatMessage, formatNumber} = useIntl()
  const [updateLabel, setUpdateLabel] = useState(() => () => {})
  const [submit, setSubmit] = useState()
  const [showPendingChangesModal, setShowPendingChangesModal] = useState(false)
  const [filterOptions, setFilterOptions] = useState<FilterCheckboxOption[]>([])
  const {data: billingUnits} = useBillingUnitsQuery(
    {
      businessId: authentication.business().uuid
    },
    {
      fetchPolicy: "cache-and-network"
    }
  )
  const {data, loading} = useCustomRewardQuery(
    {
      uuid: props.match.params.uuid
    },
    {fetchPolicy: "cache-and-network", skip: !props.match.params.uuid}
  )
  const [createCustomReward, {error: createError, errorMsg: createErrorMsg}] = useCreateCustomRewardMutation({
    onCompleted() {
      History.replace(SETTINGS_CUSTOM_REWARDS, context)
    }
  })
  const [updateCustomReward, {error: updateError, errorMsg: updateErrorMsg}] = useUpdateCustomRewardMutation({
    onCompleted() {
      History.replace(SETTINGS_CUSTOM_REWARDS, context)
    }
  })
  const [formValues, setFormValues] = useState(defaultState)
  useEffect(() => {
    if (!billingUnits?.length) return
    const buFilterOptions = billingUnits?.map<FilterCheckboxOption>((bu) => ({
      name: teamName(bu.team)!,
      uuid: bu.team.uuid!,
      value: bu.team.uuid!,
      isChecked: !data || (data?.locations && !data?.regions) ? true : data?.locations.includes(bu.team.uuid!)
    }))
    setFilterOptions(buFilterOptions)
    if (data) {
      setFormValues({
        title: data.title,
        policy: data.policy,
        redemptionInstructions: data.redemptionInstructions
      })
    }
  }, [data, billingUnits])

  const onChangeValue = (name: string, value: string) => {
    setFormValues({
      ...formValues,
      [name]: value
    })
  }

  const handleSelectFilter = ({target}: ChangeEvent<HTMLInputElement>, {selectAll = false}) => {
    const checkedFilterOptions = filterOptions.map((option) => {
      if (`${option.value}` === target.id || selectAll) {
        option.isChecked = target.checked
      }

      return option
    })

    setFilterOptions(checkedFilterOptions)
    updateLabel()
  }
  const getVariablesToSend = () => {
    const checkedLocations = filterOptions.filter(({isChecked}) => isChecked)
    const isAll = checkedLocations.length === billingUnits?.length
    const inputVariables = {
      locations: isAll ? [ALL_LOCATIONS] : checkedLocations.map(({uuid}) => uuid),
      ...formValues,
      ...(isEdit
        ? {
            uuid: props.match.params.uuid
          }
        : {})
    }
    return inputVariables
  }
  const handleSubmitCustomReward = () => {
    const mutationCall = isEdit ? updateCustomReward : createCustomReward
    const submitPromise = mutationCall({
      variables: {
        input: getVariablesToSend()
      }
    })
    setSubmit(submitPromise)
    return submitPromise
  }
  const handleCancel = () => {
    const initialFormState = {
      title: data?.title || "",
      policy: data?.policy || "",
      redemptionInstructions: data?.redemptionInstructions || ""
    }
    const initialState = {
      locations: [ALL_LOCATIONS],
      ...initialFormState,
      ...(isEdit
        ? {
            uuid: props.match.params.uuid
          }
        : {})
    }
    const hasPendingChanges = JSON.stringify(initialState) !== JSON.stringify(getVariablesToSend())
    if (hasPendingChanges) {
      setShowPendingChangesModal(true)
      return
    }
    History.replace(SETTINGS_CUSTOM_REWARDS, context)
  }
  const handleAbandon = () => History.replace(SETTINGS_CUSTOM_REWARDS, context)
  const heading = isEdit ? "admin.customRewards.edit.heading" : "admin.customRewards.create.heading"
  const actionBtn = isEdit ? "platform.button.edit" : "platform.button.create"
  return (
    <Loading isLoading={isEdit && loading}>
      <Flex direction="column" gap={3}>
        <H2>
          <FormattedMessage id={heading} />
        </H2>
        <Row>
          <Flex direction="column" gap={3}>
            <Input
              label="admin.customRewards.create.lbl.rewardTitle"
              placeholder="admin.customRewards.create.placeholder.rewardTitle"
              value={formValues.title}
              maxLength={REWARD_TITLE_MAX_LENGTH}
              name="title"
              flush
              validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"})).max(
                REWARD_TITLE_MAX_LENGTH,
                formatMessage({id: "platform.defaultValidation.maxLength"}, {count: formatNumber(REWARD_TITLE_MAX_LENGTH)})
              )}
              onChange={(value) => onChangeValue("title", value)}
            />
            <Col xs={12}>
              <Body small marginBottom={1}>
                <FormattedMessage id="admin.customRewards.create.lbl.locations" />
              </Body>
              <StyledFilterBy
                setUpdateLabel={setUpdateLabel}
                id="filter_locations"
                loadFilterOptions={null}
                filterOptions={filterOptions}
                allOptionsSelectedLabel={formatMessage({id: "platform.lbl.all"})}
                multipleOptionsSelectedLabel="admin.customRewards.filterBy.locations.multipleOptionsSelected.label"
                allOptionText={formatMessage({id: "platform.lbl.all"})}
                onSelect={(e) => handleSelectFilter(e, {selectAll: false})}
                onSelectAll={(e) => handleSelectFilter(e, {selectAll: true})}
                disableActions
              />
            </Col>
            <Message
              label="admin.customRewards.create.lbl.policy"
              placeholder="admin.customRewards.create.placeholder.policy"
              name="policy"
              hideExtraActions
              defaultValues={{
                message: data?.policy
              }}
              messageLength={POLICY_MAX_LENGTH}
              onChange={({message}) => onChangeValue("policy", message)}
              validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"})).max(
                POLICY_MAX_LENGTH,
                formatMessage({id: "platform.defaultValidation.maxLength"}, {count: formatNumber(POLICY_MAX_LENGTH)})
              )}
            />
            <Message
              label="admin.customRewards.create.lbl.redemption"
              placeholder="admin.customRewards.create.placeholder.redemption"
              name="redemptionInstructions"
              hideExtraActions
              defaultValues={{
                message: data?.redemptionInstructions || ""
              }}
              messageLength={POLICY_MAX_LENGTH}
              onChange={({message}) => onChangeValue("redemptionInstructions", message)}
              validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"})).max(
                POLICY_MAX_LENGTH,
                formatMessage({id: "platform.defaultValidation.maxLength"}, {count: formatNumber(POLICY_MAX_LENGTH)})
              )}
            />
          </Flex>
        </Row>
        {(createError || updateError) && (
          <Spacing marginTop={2}>
            <Error errorMsg={createErrorMsg || updateErrorMsg} error={createError || updateError} />
          </Spacing>
        )}
        <Flex marginTop={1} gap={2} justifyContent="flex-end">
          <Button onClick={handleCancel}>
            <FormattedMessage id="platform.button.cancel" />
          </Button>
          <ValidationButton primary onClick={handleSubmitCustomReward} load={submit}>
            <FormattedMessage id={actionBtn} />
          </ValidationButton>
        </Flex>
      </Flex>
      <GenericModal show={showPendingChangesModal} critical hideActions>
        <CancelConfirm onHide={() => setShowPendingChangesModal(false)} onConfirm={handleAbandon} />
      </GenericModal>
    </Loading>
  )
}

CustomRewardForm.contextTypes = historyContext

export default CustomRewardForm
